import React, { useState, useMemo, useEffect } from 'react';
import { useCollect } from 'hooks';
import { identity, pickBy } from 'lodash';

import Show from 'components/Show';
import Nothing from 'components/Misc/Nothing';
import Progress from 'components/Misc/Progress';
import Container from 'components/Misc/Container';
import { Title } from 'components/Misc/Typography';
import Pagination from 'components/Misc/Pagination';

import Search from 'containers/Administrator/Results/Read/Search';
import Take from 'containers/Administrator/Results/Read/Take';
import DayJs from 'utils/Dayjs';

const Read = () => {
	// Store search data
	const [search, setSearch] = useState({
		name: '',
		subjectId: 0,
		courseId: 0,
		page: 1,
		state: 'reviewed'
	});

	// Store request parameters
	const parameters = useMemo(() => {
		switch (search.state) {
		case 'failed':
			return pickBy({
				eager: '[student, exam.[subject, course]]',
				'exam.subjectId': search.subjectId,
				'exam.courseId': search.courseId,
				'student.name|student.surname:likeLower': `%${search.name}%`,
				'mark:lt': 4,
				state: 'reviewed',
				page: search.page,
			}, identity);
		case 'missed':
			return pickBy({
				eager: '[student, exam.[subject, course]]',
				'exam.subjectId': search.subjectId,
				'exam.courseId': search.courseId,
				'student.name|student.surname:likeLower': `%${search.name}%`,
				'endDate:lt': DayJs().toISOString(),
				state: 'assigned',
				page: search.page,
			}, identity);
		default:
			return pickBy({
				eager: '[student, exam.[subject, course]]',
				'exam.subjectId': search.subjectId,
				'exam.courseId': search.courseId,
				'student.name|student.surname:likeLower': `%${search.name}%`,
				state: search.state,
				page: search.page,
			}, identity);
		}
	}, [search]);

	// Store requested takes
	const [[collection, progress], collect] = useCollect('takes', parameters);

	// Handle search input change
	const onSearch = ({ name, value }) => {
		// Special case for subject change
		if (name === 'subjectId') return setSearch(prev => ({
			...prev,
			[name]: value,
			courseId: 0,
			page: 0
		}));

		// Special case for page change
		if (name === 'page') return setSearch(prev => ({
			...prev,
			[name]: value
		}));

		setSearch(prev => ({
			...prev,
			[name]: value,
			page: 1
		}));
	};

	// Collect programs on mount and on parameter change
	useEffect(() => {
		(async () => await collect())();
	}, [collect]);

	return (
		<div className="space-y-4">
			{/* Collection page's header */}
			<div className="flex justify-between">
				<Title
					title="Pārbaudes darbu rezultāti"
					className="text-2xl whitespace-nowrap text-ellipsis overflow-hidden"
				/>
			</div>

			{/* Search container */}
			<Search search={search} onChange={onSearch} />

			{/* Show loading progress */}
			<Progress progress={progress} />

			{/* Show nothing found */}
			<Nothing visible={!collection?.takes?.length && !progress} />

			{/* Show collection if takes collected */}
			<Show if={collection?.takes?.length && !progress}>
				<Container>
					<div className="table w-full table-auto">
						{collection?.takes?.map(take =>
							<Take key={take.id} take={take}	/>
						)}
					</div>
				</Container>
			</Show>

			{/* Collection's pagination */}
			<Pagination
				progress={progress}
				total={collection?.total}
				active={search.page}
				onChange={onSearch}
				className="mt-4 mx-auto"
			/>
		</div>
	);
};

export default Read;